import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import type { RouterOutputs } from "@/trpc";
import { mapSource } from "@/utils/valueMap";

dayjs.extend(relativeTime);

export type TableroLead = RouterOutputs["leads"]["listActiveByPosition"]["data"][number];

export type InactivoLead = RouterOutputs["leads"]["listInactiveByProgress"]["data"][number];

// Board row served by the non-realtime search endpoint (real-time switch OFF).
export type TableroBoardRow =
  RouterOutputs["leads"]["searchActiveNonRealtime"]["data"][number];

// The fields the card/row components actually render — satisfied by both the
// DynamoDB rows (TableroLead) and the non-realtime search rows (TableroBoardRow, whose
// serialized optionals include null).
export type TableroCardLead = {
  id: string;
  name?: string | null;
  // Absent for a sender Meta never gave us a number for; the card falls back to
  // the username, then to "Sin nombre" (contactDisplay / leadDisplayName).
  phone?: string | null;
  // Present on the DynamoDB rows only; the non-realtime search rows come from
  // Tinybird, which has no such column. Optional so both still satisfy this type —
  // a search row falls back to "Sin nombre" rather than showing a raw BSUID.
  whatsappUsername?: string | null;
  whatsappUserId?: string | null;
  source?: string | null;
  employeeId?: string | null;
  // Present on the DynamoDB rows only (the Tinybird search rows have no such
  // column). Absent means either "no AI agent owns this lead" or "this row
  // cannot say" — only the real-time board, whose rows always carry it, may
  // read it as the former.
  assignedAgentId?: string | null;
  pipelinePositionId: string;
  lastMessageSource?: "customer" | "ai_agent" | "human_agent" | null;
  lastMessagePreview?: string | null;
  lastLeadMessageAt?: string | null;
  tagIds?: string[] | null;
};

// Badge v1: list rows lack the conversation's aiActive flag, so who-attends is
// derived from employeeId + lastMessageSource alone.
// "api" is the inert case: an OTP/auth lead has no thread and never engages the AI, so it
// must not fall through to the "IA esperando" default. The lead's source is "merchant-api"
// (hyphen — the sourceId authTemplateSend writes); note the MESSAGE source is "merchant_api".
export type TableroBadge = "ia-active" | "ia-waiting" | "human-active" | "api";

export const deriveBadge = (lead: TableroCardLead): TableroBadge => {
  if (lead.employeeId) return "human-active";
  if (lead.source === "merchant-api") return "api";
  return lead.lastMessageSource === "ai_agent" ? "ia-active" : "ia-waiting";
};

export const leadNeedsReply = (lead: TableroCardLead): boolean =>
  lead.lastMessageSource === "customer";

export type Stage = "new" | "pending" | "success" | "failure";

export const STAGE_ORDER: Stage[] = ["new", "pending", "success", "failure"];

// "new" columns are driven by WhatsApp message status — manual moves there
// would be overridden, so DnD is limited to the human-managed stages.
export const MOVABLE_STAGES: ReadonlySet<string> = new Set(["pending", "success", "failure"]);

export const headerColorForStage = (stage: string): string => {
  if (stage === "success") return "bg-success-light";
  if (stage === "failure") return "bg-danger-light";
  return "bg-neutral-100";
};

export type TableroQueryFilters = {
  startDate?: string;
  endDate?: string;
  source?: string;
  // Real-time mode is single-select (the gsi1 filter expression takes one id),
  // so the array holds at most one entry there; non-realtime search sends all.
  employeeIds?: string[];
  needsReply?: boolean;
  leadType?: string;
  campaignName?: string;
  tagIds?: string[];
};

// Pauta is lead.type, never lead.source. The filter value is ANY_PAUTA or a
// campaign's exact name (InboundCampaignRule.name).
export const ANY_PAUTA = "__any__";

export const pautaQueryFilters = (
  pauta: string | null
): Pick<TableroQueryFilters, "leadType" | "campaignName"> => {
  if (!pauta) return {};
  if (pauta === ANY_PAUTA) return { leadType: "pauta" };
  return { leadType: "pauta", campaignName: pauta };
};

// Single source of the per-column query input: the drag handler does cache
// surgery with getInfiniteData/setInfiniteData, so its keys must be built the
// exact same way as the column queries'. Only index-backed filters go here —
// source/needsReply/leadType/campaignName exist only in non-realtime search mode.
export const buildColumnQueryInput = (
  merchantId: string,
  pipelineId: string,
  pipelinePositionId: string,
  filters: TableroQueryFilters
) => ({
  merchantId,
  pipelinePositionId,
  pipelineId,
  startDate: filters.startDate,
  endDate: filters.endDate,
  employeeId: filters.employeeIds?.[0],
  limit: 25,
});

// Switcher pill accents per reserved pipeline (dot + tinted active state);
// custom pipelines fall back to gray. Board columns/cards stay neutral.
export type PipelineAccent = { dot: string; active: string };

const PIPELINE_ACCENTS: Record<string, PipelineAccent> = {
  default: {
    dot: "bg-palomma-primary",
    active: "border-palomma-primary/30 bg-palomma-primary/10 text-palomma-primary",
  },
  sourceproperties: { dot: "bg-sky-500", active: "border-sky-300 bg-sky-50 text-sky-700" },
  maintenance: { dot: "bg-orange-500", active: "border-orange-300 bg-orange-50 text-orange-700" },
  general: { dot: "bg-purple-500", active: "border-purple-300 bg-purple-50 text-purple-700" },
};

const CUSTOM_PIPELINE_ACCENT: PipelineAccent = {
  dot: "bg-neutral-400",
  active: "border-neutral-400 bg-neutral-100 text-neutral-700",
};

export const pipelineAccent = (pipelineId: string): PipelineAccent =>
  PIPELINE_ACCENTS[pipelineId] ?? CUSTOM_PIPELINE_ACCENT;

export const sourceLabel = (source: string | null | undefined): string | undefined => {
  if (!source) return undefined;
  return (mapSource as Record<string, string>)[source] ?? source;
};

export const relativeFrom = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.locale("es").fromNow() : "—";
};
